import type { settingTypes } from '@ipfabric/utils';
import { FormikMultiTextField } from 'components/FormikMultiTextField/FormikMultiTextField';
import { FormikSwitchField } from 'components/FormikSwitchField/FormikSwitchField';
import { FormikTextField } from 'components/FormikTextField/FormikTextField';
import { useLocale } from 'containers/App/LocaleContext';
import { type FormikErrors, type FormikProps, useFormik } from 'formik';
import { type FC, memo } from 'react';
import { validateSlug } from 'utils/formValidation/validations';
import { makeFormikOnSubmit } from 'utils/formik/makeFormikOnSubmit';
import { hasHttpOrHttps } from 'utils/hasHttpOrHtttps';
import { VendorDialogContainer } from './VendorDialogContainer';
import { FormikAdvancedSettings } from './fields/FormikAdvancedSettings';
import { FormikRateLimitingSettingsFields } from './fields/FormikRateLimitingSettingsFields';
import { FormikVendorApiBaseUrlField } from './fields/FormikVendorApiBaseUrlField';
import { FormikVendorApiCommentField } from './fields/FormikVendorApiCommentField';
import { FormikVendorApiEnabledField } from './fields/FormikVendorApiEnabledField';
import { FormikVendorApiRespectSystemProxyConfigurationField } from './fields/FormikVendorApiRejectUnauthorizedField';
import { FormikVendorApiRejectUnauthorizedField } from './fields/FormikVendorApiRespectSystemProxyConfigurationField';
import { FormikVendorApiSlugField } from './fields/FormikVendorApiSlugField';
import { FormikVendorApiUsernamePasswordFields } from './fields/FormikVendorApiUsernamePasswordFields';
import { getAdvancedSettingsDefaultValues } from './helpers/applyDefaultInitialValues';
import { isVendorUpdate } from './helpers/guardTypes';
import type { BaseVendorFormValues, VendorDialogProps } from './vendorTypes';

type AuthenticationType = 'login' | 'apiKey';

export type CheckPointFormValues =
  BaseVendorFormValues<settingTypes.CheckPointApiSettings> &
    settingTypes.ApiKeyAuthentication &
    settingTypes.UsernamePasswordAuthentication & {
      authentication?: AuthenticationType;
    };

type Props = VendorDialogProps<
  CheckPointFormValues,
  settingTypes.CheckPointApiSettings,
  settingTypes.UpdateCheckPointApiSettings
>;

const MIN_PORT = 1;
const MAX_PORT = 65535;

const resetSecretAttributes = (
  values: CheckPointFormValues,
): CheckPointFormValues => {
  return {
    ...values,
    apiKey: '',
    username: '',
    password: '',
  };
};

const BareCheckpointApiForm: FC<Props> = (props) => {
  const editMode = props.dialogType === 'edit';
  const msg = useLocale();
  const {
    discoverySettings: {
      vendorsApi: { checkpointApiForm: checkpointApiFormMsg },
    },
  } = msg;
  const formik = useFormik<CheckPointFormValues>({
    initialValues: resetSecretAttributes({
      ...props.initialValues,
      authentication: 'login',
    }),
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TBD eventually cleanup
    validate: (values) => {
      const errors: FormikErrors<CheckPointFormValues> = {};
      const { required } = checkpointApiFormMsg.validation;

      if (values.authentication === 'login') {
        if (!editMode && !values.username) {
          errors.username = required;
        }
        if (!editMode && !values.password) {
          errors.password = required;
        }
      }

      if (values.authentication === 'apiKey') {
        if (!editMode && !values.apiKey) {
          errors.apiKey = required;
        }
      }

      if (!hasHttpOrHttps(values.baseUrl)) {
        errors.baseUrl = checkpointApiFormMsg.validation.baseUrlStartsWith;
      } else if (/:([0-9])+$/.test(values.baseUrl)) {
        const matchedPort = values.baseUrl.match(/([0-9])+$/);
        if (matchedPort?.[0]) {
          const parsedPort = Number.parseInt(matchedPort[0], 10);
          if (parsedPort < MIN_PORT || parsedPort > MAX_PORT) {
            errors.baseUrl = checkpointApiFormMsg.validation.portRange;
          }
        }
      }
      validateSlug(values, errors, msg);
      return errors;
    },
    onSubmit: makeFormikOnSubmit(
      ({ authentication, ...values }: CheckPointFormValues) => {
        const { username, password, apiKey, ...formattedValues } = {
          ...values,
          ...getAdvancedSettingsDefaultValues(values),
        };

        if (isVendorUpdate(values) && props.dialogType === 'edit') {
          if (authentication === 'login') {
            props.onSubmit({
              ...formattedValues,
              id: values.id,
              username: username || formik.initialValues.username || undefined,
              password: password || formik.initialValues.password || undefined,
            });
          } else {
            props.onSubmit({
              ...formattedValues,
              id: values.id,
              apiKey: apiKey || formik.initialValues.apiKey || undefined,
            });
          }
        } else if (props.dialogType === 'add') {
          if (authentication === 'login') {
            props.onSubmit({
              ...formattedValues,
              username,
              password,
            });
          } else {
            props.onSubmit({
              ...formattedValues,
              apiKey,
            });
          }
        }
      },
    ),
  });
  return (
    <VendorDialogContainer
      formik={formik as FormikProps<CheckPointFormValues>}
      dialogType={props.dialogType}
      {...props.vendorDialogContainerPassthruProps}
    >
      <p>{checkpointApiFormMsg.versionLabel}</p>
      <FormikVendorApiEnabledField formik={formik} />
      <FormikSwitchField
        name='authentication'
        label='* Authentication'
        formik={formik}
        options={[
          {
            label: 'Username / Password',
            value: 'login',
          },
          {
            label: 'API Key',
            value: 'apiKey',
          },
        ]}
        size='small'
      />
      {formik.values.authentication === 'apiKey' && (
        <FormikTextField
          formik={formik}
          label={
            <div className='flex justify-between'>
              <div>{checkpointApiFormMsg.apiKey}</div>
              <div className='text-danger ml-4 font-normal'>
                {checkpointApiFormMsg.apiKeyHelp}
              </div>
            </div>
          }
          placeholder={editMode ? '(unchanged)' : undefined}
          name='apiKey'
          type='text'
        />
      )}
      {formik.values.authentication === 'login' && (
        <FormikVendorApiUsernamePasswordFields
          formik={formik}
          isEditMode={editMode}
        />
      )}
      <FormikVendorApiBaseUrlField
        formik={formik}
        placeholder='https://management-server.domain.tld'
      />
      <FormikMultiTextField
        formik={formik}
        label={
          <>
            <div className='flex justify-between'>
              <div>{checkpointApiFormMsg.domains}</div>
              <div className='ml-4 font-normal'>
                {checkpointApiFormMsg.domainsDescription}
              </div>
            </div>
            <div className='help-block'>{checkpointApiFormMsg.domainsHelp}</div>
          </>
        }
        name='domains'
      />
      <FormikVendorApiSlugField formik={formik} />
      <FormikVendorApiCommentField formik={formik} />
      <FormikVendorApiRespectSystemProxyConfigurationField formik={formik} />
      <FormikVendorApiRejectUnauthorizedField formik={formik} />
      <FormikAdvancedSettings>
        <FormikRateLimitingSettingsFields formik={formik} />
      </FormikAdvancedSettings>
    </VendorDialogContainer>
  );
};

export const CheckpointApiForm = memo(BareCheckpointApiForm);
